import { setSyncStatus } from '@storeOld/reducers/ui'

import type { SyncStatus } from '@/types'

type CommitSyncStatusStore = {
  dispatch: (action: ReturnType<typeof setSyncStatus>) => void
  getState: () => {
    ui: {
      syncStatus: SyncStatus
    }
  }
}

type CommitSyncStatusContext = {
  store: CommitSyncStatusStore
  hideSyncStatusTimeoutId: ReturnType<typeof setTimeout> | null
  showSuccessTimeoutId: ReturnType<typeof setTimeout> | null
  syncingStartedAt: number
}

const SYNC_SUCCESS_HIDE_DELAY = 1200
const SYNCING_MIN_DURATION = 800

const clearHideSyncStatusTimeout = (ctx: CommitSyncStatusContext): void => {
  if (ctx.hideSyncStatusTimeoutId !== null) {
    clearTimeout(ctx.hideSyncStatusTimeoutId)
    ctx.hideSyncStatusTimeoutId = null
  }
}

const clearShowSuccessTimeout = (ctx: CommitSyncStatusContext): void => {
  if (ctx.showSuccessTimeoutId !== null) {
    clearTimeout(ctx.showSuccessTimeoutId)
    ctx.showSuccessTimeoutId = null
  }
}

const showSuccessWithAutoHide = (ctx: CommitSyncStatusContext): void => {
  clearHideSyncStatusTimeout(ctx)
  ctx.showSuccessTimeoutId = null

  ctx.store.dispatch(setSyncStatus('success'))

  ctx.hideSyncStatusTimeoutId = setTimeout(() => {
    ctx.hideSyncStatusTimeoutId = null

    if (ctx.store.getState().ui.syncStatus !== 'success') {
      return
    }

    ctx.store.dispatch(setSyncStatus('hidden'))
  }, SYNC_SUCCESS_HIDE_DELAY)
}

const startSyncing = (ctx: CommitSyncStatusContext): void => {
  clearShowSuccessTimeout(ctx)
  clearHideSyncStatusTimeout(ctx)
  ctx.syncingStartedAt = Date.now()
  ctx.store.dispatch(setSyncStatus('syncing'))
}

const completeSyncing = (ctx: CommitSyncStatusContext): void => {
  const elapsed = Date.now() - ctx.syncingStartedAt
  const remainingDelay = Math.max(0, SYNCING_MIN_DURATION - elapsed)

  clearShowSuccessTimeout(ctx)
  if (remainingDelay === 0) {
    showSuccessWithAutoHide(ctx)
    return
  }

  ctx.showSuccessTimeoutId = setTimeout(() => {
    showSuccessWithAutoHide(ctx)
  }, remainingDelay)
}

export const createCommitSyncStatus = (store: CommitSyncStatusStore) => {
  const ctx: CommitSyncStatusContext = {
    store,
    hideSyncStatusTimeoutId: null,
    showSuccessTimeoutId: null,
    syncingStartedAt: 0,
  }

  return {
    setSyncingStatus: () => startSyncing(ctx),
    setSuccessStatusWithAutoHide: () => completeSyncing(ctx),
  }
}
